import { nodeCrypto } from "./node-crypto-lazy.ts";

import { z } from "zod";

import {
  SourceStructureSelectionV1Schema,
  evaluateLibraryReuseAdmissionReceiptV2,
  evaluateLocalEvaluationRightsReceiptV2,
} from "./admission-v2.ts";
import {
  validateOpenPencilFactShardManifestV2,
  verifyOpenPencilStructuralProjection,
  type OpenPencilFactShardManifestV2,
  type OpenPencilStructuralFactShardV1,
  type Sha256,
  type StructuralFact,
} from "./fact-shards.ts";

const NAMESPACE = "cogo.openpencil.structural.v1";
const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/;
const PROJECTION_KEY_PATTERN = /^projection-key:[a-f0-9]{16}$/;
const PURPOSE_CLASS = {
  "design-token": "semantic-token-roles",
  component: "component-roles-slots-variants-states",
} as const;
const ALLOWED_EXTRACTION_CLASSES = [
  "normalized-token-values",
  "semantic-token-roles",
  "component-roles-slots-variants-states",
  "accessibility-and-interaction-affordances",
  "layout-constraints-and-structure-patterns",
  "source-to-cogo-role-mappings",
] as const;
const PROHIBITED_EXTRACTION_CLASSES = [
  "raster-asset-bytes",
  "vector-asset-bytes-or-paths",
  "raw-fig-bytes",
  "exact-geometry-replica",
  "unbounded-source-text-corpus",
  "parse-unstable-node-identities",
  "unadmitted-behavior-or-business-rules",
] as const;

const sha256Schema = z.string().regex(SHA256_PATTERN);
const contentRefSchema = z
  .object({ id: z.string().min(1), contentHash: sha256Schema })
  .strict();
const sourceSchema = z
  .object({
    kind: z.literal("local-figma-snapshot"),
    sourceId: z.string().min(1),
    communityResourceId: z.string().min(1),
    communityUrl: z.string().url(),
    contentHash: sha256Schema,
    byteLength: z.number().int().positive(),
    identityNotes: z.string().min(1),
    upstreamDriftUndetectable: z.literal(true),
  })
  .strict();
const capabilityGrantSchema = z
  .object({
    localEvaluation: z.boolean(),
    ontologyExtraction: z.boolean(),
    packDerivation: z.boolean(),
    derivativePublication: z.boolean(),
    rawRedistribution: z.boolean(),
    upstreamWriteback: z.boolean(),
  })
  .strict();
const attributionSchema = z
  .object({
    required: z.boolean(),
    text: z.string().min(1).optional(),
    sourceUrl: z.string().url().optional(),
    licenseId: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const inherited = [value.text, value.sourceUrl, value.licenseId].filter(
      (entry) => entry !== undefined,
    );
    if (
      (value.required && inherited.length !== 3) ||
      (!value.required && inherited.length !== 0)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "attribution inheritance is invalid",
      });
    }
  });
const licenseSchema = z
  .object({
    status: z.literal("verified"),
    spdx: z.string().min(1),
    evidenceRefs: z.array(contentRefSchema).min(1),
  })
  .strict();
const actorSchema = z
  .object({ kind: z.enum(["user", "service"]), actorId: z.string().min(1) })
  .strict();
const humanActorSchema = z
  .object({
    kind: z.enum(["user", "organization-admin"]),
    actorId: z.string().min(1),
  })
  .strict();
const localRightsSchema = z
  .object({
    schemaVersion: z.literal("cogo.local-evaluation-rights-receipt.v2"),
    receiptId: z.string().min(1),
    source: sourceSchema,
    evaluationScope: z
      .object({
        kind: z.literal("local-figma-evaluation-scope-v1"),
        scope: z.literal("exact-snapshot"),
        permittedOperations: z
          .array(z.enum(["headless-parse", "local-preview-render"]))
          .min(1)
          .max(2)
          .refine((value) => new Set(value).size === value.length),
        assetEgressAllowed: z.literal(false),
        networkExfiltrationAllowed: z.literal(false),
      })
      .strict(),
    license: licenseSchema,
    grant: capabilityGrantSchema,
    attributionObligation: attributionSchema,
    actor: actorSchema,
    issuedAt: z.string().datetime(),
    contentHash: sha256Schema,
  })
  .strict();
const rubricItemSchema = z
  .object({
    verdict: z.enum(["pass", "fail", "not-applicable"]),
    evidenceRefs: z.array(contentRefSchema).min(1),
  })
  .strict();
const rubricSchema = z
  .object({
    targetUserAndJob: rubricItemSchema,
    informationArchitectureAndHierarchy: rubricItemSchema,
    responsiveVariants: rubricItemSchema,
    interactionStates: rubricItemSchema,
    accessibility: rubricItemSchema,
    realisticContent: rubricItemSchema,
    designSystemConsistency: rubricItemSchema,
    brandAndAssetRights: rubricItemSchema,
    creatorAdoptionAppeal: rubricItemSchema,
  })
  .strict();
const proposalSchema = z
  .object({
    schemaVersion: z.literal("cogo.figma-quality-evidence-proposal.v2"),
    proposalId: z.string().min(1),
    source: sourceSchema,
    selection: SourceStructureSelectionV1Schema,
    localEvaluationRightsRef: contentRefSchema,
    rubricVersion: z.literal("figma-quality-rubric.v1"),
    rubric: rubricSchema,
    proposedOutcome: z.enum(["adopt", "reject"]),
    admission: z
      .object({
        activationAllowed: z.literal(false),
        requiredDecisionAuthority: z.literal("user-or-organization-admin"),
        modelRole: z.literal("bounded-evidence-only"),
      })
      .strict(),
    evidenceRefs: z.array(contentRefSchema).min(1),
    generatedAt: z.string().datetime(),
    contentHash: sha256Schema,
  })
  .strict();
const decisionSchema = z
  .object({
    schemaVersion: z.literal("cogo.figma-quality-confirmation-decision.v2"),
    decisionId: z.string().min(1),
    proposalRef: contentRefSchema,
    decision: z.enum(["approved", "rejected", "changes-requested", "deferred"]),
    actor: humanActorSchema,
    decidedAt: z.string().datetime(),
    evidenceRefs: z.array(contentRefSchema).min(1),
    contentHash: sha256Schema,
  })
  .strict();
const fqReceiptSchema = z
  .object({
    schemaVersion: z.literal("cogo.figma-quality-admission-receipt.v2"),
    receiptId: z.string().min(1),
    source: sourceSchema,
    selection: SourceStructureSelectionV1Schema,
    proposalRef: contentRefSchema,
    decisionRef: contentRefSchema,
    lifecycleState: z.literal("adopted"),
    immutable: z.literal(true),
    issuedAt: z.string().datetime(),
    contentHash: sha256Schema,
  })
  .strict();
const extractionProfileSchema = z
  .object({
    profileId: z.literal("cogo.read-only-design-ontology.v1"),
    allowedClasses: z
      .array(z.enum(ALLOWED_EXTRACTION_CLASSES))
      .min(1)
      .max(ALLOWED_EXTRACTION_CLASSES.length)
      .refine((value) => new Set(value).size === value.length),
    prohibitedClasses: z
      .array(z.enum(PROHIBITED_EXTRACTION_CLASSES))
      .length(PROHIBITED_EXTRACTION_CLASSES.length)
      .refine((value) => new Set(value).size === value.length),
    assetBytesMayLeaveVault: z.literal(false),
  })
  .strict();
const reuseReceiptSchema = z
  .object({
    schemaVersion: z.literal("cogo.library-reuse-admission-receipt.v2"),
    receiptId: z.string().min(1),
    source: sourceSchema,
    selection: SourceStructureSelectionV1Schema,
    localEvaluationRightsRef: contentRefSchema,
    fqReceiptRef: contentRefSchema,
    license: licenseSchema,
    grant: capabilityGrantSchema,
    attributionObligation: attributionSchema,
    extractionProfile: extractionProfileSchema,
    actor: humanActorSchema,
    issuedAt: z.string().datetime(),
    contentHash: sha256Schema,
  })
  .strict();

export interface OpenPencilOntologyAuthorityArtifactsV2 {
  localEvaluationRightsReceipt: unknown;
  figmaQualityEvidenceProposal: unknown;
  figmaQualityConfirmationDecision: unknown;
  figmaQualityAdmissionReceipt: unknown;
  libraryReuseAdmissionReceipt: unknown;
}

export interface OpenPencilOntologyBoundaryInputV2 extends OpenPencilOntologyAuthorityArtifactsV2 {
  loadLocalReadback: () => unknown;
  structuralManifest: OpenPencilFactShardManifestV2;
  structuralBodies: OpenPencilStructuralFactShardV1[];
  projectionKey: Uint8Array;
}

type ContentHashRef = { contentHash: Sha256 };

export interface OpenPencilOntologySourceRefsV2 {
  sourceArtifactHash: Sha256;
  structuralProjectionHash: Sha256;
  projectionKeyId: `projection-key:${string}`;
  localEvaluationRightsReceiptRef: ContentHashRef;
  figmaQualityEvidenceProposalRef: ContentHashRef;
  figmaQualityConfirmationDecisionRef: ContentHashRef;
  figmaQualityAdmissionReceiptRef: ContentHashRef;
  libraryReuseAdmissionReceiptRef: ContentHashRef;
}

type Purpose = keyof typeof PURPOSE_CLASS;
type JsonRecord = Record<string, unknown>;
type ParsedAuthority = {
  localRights: z.infer<typeof localRightsSchema>;
  proposal: z.infer<typeof proposalSchema>;
  decision: z.infer<typeof decisionSchema>;
  fqReceipt: z.infer<typeof fqReceiptSchema>;
  reuseReceipt: z.infer<typeof reuseReceiptSchema>;
};

const preflightBrand: unique symbol = Symbol("OpenPencilOntologyPreflightV2");

export type PreflightOpenPencilOntologyBoundaryV2<P extends Purpose> =
  Readonly<{
    purpose: P;
    [preflightBrand]: true;
  }>;

export interface VerifiedOpenPencilOntologyBoundaryV2<P extends Purpose> {
  purpose: P;
  readback: unknown;
  projectionKey: Uint8Array;
  structuralManifest: OpenPencilFactShardManifestV2;
  structuralBodies: readonly OpenPencilStructuralFactShardV1[];
  facts: readonly StructuralFact[];
  sourceRefs: OpenPencilOntologySourceRefsV2;
}

type OriginalInputRefs = {
  localEvaluationRightsReceipt: unknown;
  figmaQualityEvidenceProposal: unknown;
  figmaQualityConfirmationDecision: unknown;
  figmaQualityAdmissionReceipt: unknown;
  libraryReuseAdmissionReceipt: unknown;
  manifest: OpenPencilFactShardManifestV2;
  bodies: OpenPencilStructuralFactShardV1[];
  bodyMembers: ReadonlySet<OpenPencilStructuralFactShardV1>;
  projectionKey: Uint8Array;
};

type PreflightState = {
  purpose: Purpose;
  input: OpenPencilOntologyBoundaryInputV2;
  inputFingerprint: string;
  snapshotFingerprint: string;
  originalRefs: OriginalInputRefs;
  loader: () => unknown;
  projectionKey: Uint8Array;
  manifest: OpenPencilFactShardManifestV2;
  bodies: readonly OpenPencilStructuralFactShardV1[];
  bodyMap: ReadonlyMap<string, unknown>;
  facts: readonly StructuralFact[];
  sourceRefs: OpenPencilOntologySourceRefsV2;
  consumed: boolean;
};

const preflightStates = new WeakMap<object, PreflightState>();

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function canonicalize(value: unknown, seen = new WeakSet<object>()): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_NON_FINITE_NUMBER");
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => canonicalize(entry, seen));
  }
  if (!isRecord(value)) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_CANONICAL_JSON_INVALID");
  }
  if (seen.has(value)) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_CANONICAL_JSON_CYCLE");
  }
  seen.add(value);
  const result: JsonRecord = {};
  for (const key of Object.keys(value).sort()) {
    const child = value[key];
    if (child !== undefined) result[key] = canonicalize(child, seen);
  }
  seen.delete(value);
  return result;
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function deepFreeze<T>(value: T, seen = new WeakSet<object>()): T {
  if (
    value === null ||
    typeof value !== "object" ||
    value instanceof Uint8Array ||
    seen.has(value)
  ) {
    return value;
  }
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((entry) => deepFreeze(entry, seen));
  } else {
    Object.values(value as JsonRecord).forEach((entry) =>
      deepFreeze(entry, seen),
    );
  }
  return Object.freeze(value);
}

function projectionKeyId(key: Uint8Array): `projection-key:${string}` {
  if (
    !(key instanceof Uint8Array) ||
    key.byteLength !== 32 ||
    key.every((byte) => byte === 0)
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_PROJECTION_KEY_INVALID");
  }
  return `projection-key:${nodeCrypto().createHmac("sha256", key)
    .update(canonicalJson([NAMESPACE, "projection-key-id"]), "utf8")
    .digest("hex")
    .slice(0, 16)}`;
}

function contentHashRef(value: string): ContentHashRef {
  if (!SHA256_PATTERN.test(value)) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_CONTENT_HASH_INVALID");
  }
  return { contentHash: value as Sha256 };
}

function parseAuthority(
  input: OpenPencilOntologyBoundaryInputV2,
): ParsedAuthority {
  const localRights = localRightsSchema.safeParse(
    input.localEvaluationRightsReceipt,
  );
  const proposal = proposalSchema.safeParse(input.figmaQualityEvidenceProposal);
  const decision = decisionSchema.safeParse(
    input.figmaQualityConfirmationDecision,
  );
  const fqReceipt = fqReceiptSchema.safeParse(
    input.figmaQualityAdmissionReceipt,
  );
  const reuseReceipt = reuseReceiptSchema.safeParse(
    input.libraryReuseAdmissionReceipt,
  );
  if (
    !localRights.success ||
    !proposal.success ||
    !decision.success ||
    !fqReceipt.success ||
    !reuseReceipt.success
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_AUTHORITY_SCHEMA_INVALID");
  }
  return {
    localRights: localRights.data,
    proposal: proposal.data,
    decision: decision.data,
    fqReceipt: fqReceipt.data,
    reuseReceipt: reuseReceipt.data,
  };
}

function bindBodies(
  manifest: OpenPencilFactShardManifestV2,
  bodiesInput: unknown,
): {
  bodies: OpenPencilStructuralFactShardV1[];
  map: Map<string, unknown>;
} {
  if (
    !Array.isArray(manifest.shards) ||
    manifest.shards.length === 0 ||
    !Array.isArray(bodiesInput)
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_BODY_SET_INVALID");
  }
  const bodiesByKind = new Map<string, OpenPencilStructuralFactShardV1>();
  for (const body of bodiesInput) {
    if (
      !isRecord(body) ||
      typeof body.kind !== "string" ||
      bodiesByKind.has(body.kind)
    ) {
      throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_BODY_SET_INVALID");
    }
    bodiesByKind.set(
      body.kind,
      body as unknown as OpenPencilStructuralFactShardV1,
    );
  }
  const shardsByKind = new Map<string, (typeof manifest.shards)[number]>();
  for (const shard of manifest.shards) {
    if (
      !isRecord(shard) ||
      typeof shard.kind !== "string" ||
      shardsByKind.has(shard.kind)
    ) {
      throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_BODY_SET_INVALID");
    }
    shardsByKind.set(shard.kind, shard);
  }
  if (
    bodiesByKind.size !== shardsByKind.size ||
    [...bodiesByKind.keys()].some((kind) => !shardsByKind.has(kind))
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_BODY_SET_MISMATCH");
  }
  const bodies = [...bodiesByKind.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([, body]) => body);
  const map = new Map<string, unknown>();
  for (const shard of manifest.shards) {
    const body = bodiesByKind.get(shard.kind);
    if (!body) {
      throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_BODY_SET_MISMATCH");
    }
    map.set(shard.kind, body);
    map.set(shard.artifactRef, body);
    map.set(shard.contentHash, body);
  }
  return { bodies, map };
}

function inputFingerprint(input: OpenPencilOntologyBoundaryInputV2): string {
  return nodeCrypto().createHash("sha256")
    .update(
      canonicalJson({
        authority: [
          input.localEvaluationRightsReceipt,
          input.figmaQualityEvidenceProposal,
          input.figmaQualityConfirmationDecision,
          input.figmaQualityAdmissionReceipt,
          input.libraryReuseAdmissionReceipt,
        ],
        manifest: input.structuralManifest,
        bodies: [...input.structuralBodies].sort((left, right) =>
          left.kind < right.kind ? -1 : left.kind > right.kind ? 1 : 0,
        ),
        projectionKey: Array.from(input.projectionKey),
      }),
      "utf8",
    )
    .digest("hex");
}

function stateSnapshotFingerprint(
  manifest: OpenPencilFactShardManifestV2,
  bodies: readonly OpenPencilStructuralFactShardV1[],
  projectionKey: Uint8Array,
  sourceRefs: OpenPencilOntologySourceRefsV2,
): string {
  return nodeCrypto().createHash("sha256")
    .update(
      canonicalJson({
        manifest,
        bodies,
        projectionKey: Array.from(projectionKey),
        sourceRefs,
      }),
      "utf8",
    )
    .digest("hex");
}

function originalRefsMatch(
  input: OpenPencilOntologyBoundaryInputV2,
  refs: OriginalInputRefs,
): boolean {
  return (
    input.localEvaluationRightsReceipt === refs.localEvaluationRightsReceipt &&
    input.figmaQualityEvidenceProposal === refs.figmaQualityEvidenceProposal &&
    input.figmaQualityConfirmationDecision ===
      refs.figmaQualityConfirmationDecision &&
    input.figmaQualityAdmissionReceipt === refs.figmaQualityAdmissionReceipt &&
    input.libraryReuseAdmissionReceipt === refs.libraryReuseAdmissionReceipt &&
    input.structuralManifest === refs.manifest &&
    input.structuralBodies === refs.bodies &&
    input.projectionKey === refs.projectionKey &&
    input.structuralBodies.length === refs.bodyMembers.size &&
    input.structuralBodies.every((body) => refs.bodyMembers.has(body))
  );
}

function captureOriginalRefs(
  input: OpenPencilOntologyBoundaryInputV2,
): OriginalInputRefs {
  if (
    !isRecord(input.structuralManifest) ||
    !Array.isArray(input.structuralBodies) ||
    !(input.projectionKey instanceof Uint8Array)
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_INPUT_INVALID");
  }
  return {
    localEvaluationRightsReceipt: input.localEvaluationRightsReceipt,
    figmaQualityEvidenceProposal: input.figmaQualityEvidenceProposal,
    figmaQualityConfirmationDecision: input.figmaQualityConfirmationDecision,
    figmaQualityAdmissionReceipt: input.figmaQualityAdmissionReceipt,
    libraryReuseAdmissionReceipt: input.libraryReuseAdmissionReceipt,
    manifest: input.structuralManifest,
    bodies: input.structuralBodies,
    bodyMembers: new Set(input.structuralBodies),
    projectionKey: input.projectionKey,
  };
}

function stateIsCurrent(
  state: PreflightState,
  purpose: Purpose,
  input: OpenPencilOntologyBoundaryInputV2,
): boolean {
  return (
    state.purpose === purpose &&
    state.input === input &&
    state.loader === input.loadLocalReadback &&
    originalRefsMatch(input, state.originalRefs) &&
    state.inputFingerprint === inputFingerprint(input) &&
    state.snapshotFingerprint ===
      stateSnapshotFingerprint(
        state.manifest,
        state.bodies,
        state.projectionKey,
        state.sourceRefs,
      )
  );
}

async function preflight<P extends Purpose>(
  purpose: P,
  input: OpenPencilOntologyBoundaryInputV2,
): Promise<PreflightOpenPencilOntologyBoundaryV2<P>> {
  if (!input || typeof input.loadLocalReadback !== "function") {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_INPUT_INVALID");
  }
  const originalRefs = captureOriginalRefs(input);
  const authority = parseAuthority(input);
  const initialInputFingerprint = inputFingerprint(input);
  const localEvaluation = await evaluateLocalEvaluationRightsReceiptV2(
    authority.localRights,
  );
  const reuseEvaluation = await evaluateLibraryReuseAdmissionReceiptV2(
    authority.localRights,
    authority.proposal,
    authority.decision,
    authority.fqReceipt,
    authority.reuseReceipt,
  );
  if (!localEvaluation.passed || !reuseEvaluation.passed) {
    throw new Error(
      `OPENPENCIL_ONTOLOGY_BOUNDARY_AUTHORITY_REJECTED:${[
        ...localEvaluation.errors,
        ...reuseEvaluation.errors,
      ].join("|")}`,
    );
  }
  if (
    authority.reuseReceipt.grant.ontologyExtraction !== true ||
    authority.reuseReceipt.extractionProfile.assetBytesMayLeaveVault !==
      false ||
    !authority.reuseReceipt.extractionProfile.allowedClasses.includes(
      PURPOSE_CLASS[purpose],
    )
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_PURPOSE_PROFILE_REJECTED");
  }
  if (
    !isRecord(input.structuralManifest) ||
    input.structuralManifest.schemaVersion !==
      "cogo.openpencil-fact-shard-manifest.v2"
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_MANIFEST_INVALID");
  }
  const manifest = input.structuralManifest;
  const bound = bindBodies(manifest, input.structuralBodies);
  const manifestIssues = validateOpenPencilFactShardManifestV2(
    manifest,
    bound.map,
  );
  if (manifestIssues.length > 0) {
    throw new Error(
      `OPENPENCIL_ONTOLOGY_BOUNDARY_STRUCTURAL_PRECHECK_REJECTED:${manifestIssues.join("|")}`,
    );
  }
  const derivedKeyId = projectionKeyId(input.projectionKey);
  if (
    !PROJECTION_KEY_PATTERN.test(manifest.projectionKeyId) ||
    manifest.projectionKeyId !== derivedKeyId
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_PROJECTION_KEY_MISMATCH");
  }
  if (
    manifest.sourceArtifactHash !== authority.localRights.source.contentHash
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_SOURCE_ARTIFACT_MISMATCH");
  }
  if (
    !originalRefsMatch(input, originalRefs) ||
    initialInputFingerprint !== inputFingerprint(input)
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_INPUT_MUTATED");
  }
  const sourceRefs: OpenPencilOntologySourceRefsV2 = {
    sourceArtifactHash: manifest.sourceArtifactHash,
    structuralProjectionHash: manifest.projectionHash,
    projectionKeyId: manifest.projectionKeyId,
    localEvaluationRightsReceiptRef: contentHashRef(
      authority.localRights.contentHash,
    ),
    figmaQualityEvidenceProposalRef: contentHashRef(
      authority.proposal.contentHash,
    ),
    figmaQualityConfirmationDecisionRef: contentHashRef(
      authority.decision.contentHash,
    ),
    figmaQualityAdmissionReceiptRef: contentHashRef(
      authority.fqReceipt.contentHash,
    ),
    libraryReuseAdmissionReceiptRef: contentHashRef(
      authority.reuseReceipt.contentHash,
    ),
  };
  const manifestSnapshot = structuredClone(manifest);
  const bodySnapshots = structuredClone(bound.bodies);
  const projectionKeySnapshot = input.projectionKey.slice();
  const snapshotBound = bindBodies(manifestSnapshot, bodySnapshots);
  const snapshotIssues = validateOpenPencilFactShardManifestV2(
    manifestSnapshot,
    snapshotBound.map,
  );
  if (snapshotIssues.length > 0) {
    throw new Error(
      `OPENPENCIL_ONTOLOGY_BOUNDARY_SNAPSHOT_INVALID:${snapshotIssues.join("|")}`,
    );
  }
  const frozenManifest = deepFreeze(manifestSnapshot);
  const frozenBodies = deepFreeze(snapshotBound.bodies);
  const frozenFacts = deepFreeze(frozenBodies.flatMap((body) => body.facts));
  const frozenSourceRefs = deepFreeze(structuredClone(sourceRefs));
  const token = Object.freeze({
    purpose,
    [preflightBrand]: true as const,
  }) as PreflightOpenPencilOntologyBoundaryV2<P>;
  preflightStates.set(token, {
    purpose,
    input,
    inputFingerprint: initialInputFingerprint,
    snapshotFingerprint: stateSnapshotFingerprint(
      frozenManifest,
      frozenBodies,
      projectionKeySnapshot,
      frozenSourceRefs,
    ),
    originalRefs,
    loader: input.loadLocalReadback,
    projectionKey: projectionKeySnapshot,
    manifest: frozenManifest,
    bodies: frozenBodies,
    bodyMap: snapshotBound.map,
    facts: frozenFacts,
    sourceRefs: frozenSourceRefs,
    consumed: false,
  });
  return token;
}

function open<P extends Purpose>(
  purpose: P,
  input: OpenPencilOntologyBoundaryInputV2,
  token: PreflightOpenPencilOntologyBoundaryV2<P>,
): VerifiedOpenPencilOntologyBoundaryV2<P> {
  const state =
    token && typeof token === "object" ? preflightStates.get(token) : undefined;
  if (!state || state.consumed) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_PREFLIGHT_INVALID");
  }
  state.consumed = true;
  if (!stateIsCurrent(state, purpose, input)) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_PREFLIGHT_INVALID");
  }
  const readback = input.loadLocalReadback();
  const verification = verifyOpenPencilStructuralProjection(
    readback,
    state.projectionKey,
    state.manifest,
    state.bodyMap,
  );
  if (!verification.valid) {
    throw new Error(
      `OPENPENCIL_ONTOLOGY_BOUNDARY_STRUCTURAL_PROJECTION_REJECTED:${verification.issues.join("|")}`,
    );
  }
  return Object.freeze({
    purpose,
    readback,
    projectionKey: state.projectionKey.slice(),
    structuralManifest: state.manifest,
    structuralBodies: state.bodies,
    facts: state.facts,
    sourceRefs: state.sourceRefs,
  });
}

export function getPreflightOpenPencilOntologyFactsV2<P extends Purpose>(
  input: OpenPencilOntologyBoundaryInputV2,
  token: PreflightOpenPencilOntologyBoundaryV2<P>,
): readonly StructuralFact[] {
  const state =
    token && typeof token === "object" ? preflightStates.get(token) : undefined;
  if (
    !state ||
    state.consumed ||
    !stateIsCurrent(state, token.purpose, input)
  ) {
    throw new Error("OPENPENCIL_ONTOLOGY_BOUNDARY_PREFLIGHT_INVALID");
  }
  return state.facts;
}

export function preflightOpenPencilDesignTokenBoundaryV2(
  input: OpenPencilOntologyBoundaryInputV2,
): Promise<PreflightOpenPencilOntologyBoundaryV2<"design-token">> {
  return preflight("design-token", input);
}

export function preflightOpenPencilComponentBoundaryV2(
  input: OpenPencilOntologyBoundaryInputV2,
): Promise<PreflightOpenPencilOntologyBoundaryV2<"component">> {
  return preflight("component", input);
}

export function openVerifiedOpenPencilDesignTokenReadbackV2(
  input: OpenPencilOntologyBoundaryInputV2,
  token: PreflightOpenPencilOntologyBoundaryV2<"design-token">,
): VerifiedOpenPencilOntologyBoundaryV2<"design-token"> {
  return open("design-token", input, token);
}

export function openVerifiedOpenPencilComponentReadbackV2(
  input: OpenPencilOntologyBoundaryInputV2,
  token: PreflightOpenPencilOntologyBoundaryV2<"component">,
): VerifiedOpenPencilOntologyBoundaryV2<"component"> {
  return open("component", input, token);
}
